import { Clock } from 'lucide-react'
import ProfileCard from './components/ProfileCard'
import {
  bgCover,
  container,
  joinOn,
  joinOnLabel,
  lastSignedIn,
  main,
  noWordWrap,
  sidebar,
  sidebarFooterItemLabel
} from './styles.css'
import { FC, PropsWithChildren } from 'react'
import SideMenu from './components/SideMenu'
import { MENU_GROUP, USER_TYPE } from './types'
import { useProfileData } from './hooks/useProfileData'
import { formatDateWithGMT } from '$app/utils'
import { useProfileDataStore } from './hooks/useProfileDataStore'
import { useShallow } from 'zustand/react/shallow'
import Loading from '$components/Loading'
import VerifyEmailCard from './components/VerifyEmailCard'
import useResponsive from '$hooks/actions/useResponsive'
import { flexColumn } from '$styles/common.css'
import { usePermissions } from '$hooks/actions'
import { PERMISSIONS } from '$app/constants'

type Props = {
  userType: USER_TYPE
  title?: string
  desc?: string
}

const UserProfileLayout: FC<PropsWithChildren<Props>> = ({
  children,
  userType
}) => {
  const responsive = useResponsive()

  const { menus, isDataFetching } = useProfileData({
    userType
  })

  const { checkPermissions } = usePermissions()
  const CAN_VIEW_EMAIL = checkPermissions([PERMISSIONS.USER_EMAIL_VIEW])

  const { created_at, last_login_at, is_verified, phone_code, phone_no } =
    useProfileDataStore(useShallow(state => state.profile)) ?? {}

  const hasPhone = !!phone_code && !!phone_no

  const isMember = userType === USER_TYPE.MEMBER

  if (isDataFetching) return <Loading />

  const isTablet = responsive.smallTablet

  const filteredMenus = hasPhone
    ? menus
    : menus
        .reduce<MENU_GROUP>((acc, menu) => {
          const { title, menus } = menu
          const _menus = menus.filter(m => m.label !== 'SMS OTP')

          return [...acc, { title, menus: _menus }]
        }, [])
        .map(m => ({
          ...m,
          menus: m.menus.filter(mm => checkPermissions(mm.permissions))
        }))

  const hideEmail = (() => {
    if (isMember) return true
    return !CAN_VIEW_EMAIL
  })()

  return (
    <article className={container}>
      {isTablet && (
        <aside className={sidebar}>
          <ProfileCard hideEmail={hideEmail} hideVerifyBadge={isMember} />

          <SideMenu menus={filteredMenus} />

          {!is_verified && !isMember && <VerifyEmailCard />}

          <article className={joinOn}>
            <span className={joinOnLabel}>Joined On</span>
            <span className={noWordWrap}>{formatDateWithGMT(created_at)}</span>
          </article>

          <article className={lastSignedIn}>
            <Clock size={16} />
            <span className={sidebarFooterItemLabel}>Last Signed In</span>
            <span className={noWordWrap}>
              {formatDateWithGMT(last_login_at)}
            </span>
          </article>
        </aside>
      )}

      {!isTablet && (
        <>
          <ProfileCard />

          <SideMenu menus={filteredMenus} />
        </>
      )}

      <main className={main}>
        <span className={bgCover} />
        <article
          className={flexColumn}
          style={{ position: 'relative', flex: 1 }}
        >
          {children}
        </article>
      </main>
    </article>
  )
}

export default UserProfileLayout
